<template>
  <div class="mt-6">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">
          JSON-LD Schema
        </h3>
        <button
          v-if="canGenerate"
          @click="startGeneration"
          :disabled="loading"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
          Generate Schema
        </button>
      </div>

      <!-- Progress -->
      <div v-if="generating" class="mb-4">
        <div class="flex items-center gap-2 text-sm text-primary-500">
          <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          <span>{{ progressMessage }}</span>
        </div>
      </div>

      <!-- Approved State -->
      <div v-if="schema?.approved" class="bg-primary-50 border border-primary-200 dark:bg-primary-900/20 dark:border-primary-700 rounded-lg p-4 mb-4">
        <div class="flex items-center gap-2">
          <svg class="h-5 w-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
          <span class="text-primary-700 dark:text-primary-300 font-medium">Schema approved — {{ schema.selected_types?.length }} types ready for deployment</span>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="schema?.status === 'failed'" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-4">
        <p class="text-sm text-red-700">
          {{ schema.error_message }}
        </p>
        <button
          @click="startGeneration"
          class="mt-2 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm hover:bg-red-200 dark:hover:bg-red-900/50">
          Retry
        </button>
      </div>

      <!-- Schema Review (status: ready) -->
      <div v-if="schema?.status === 'ready' || schema?.approved">
        <!-- Competitor Coverage -->
        <div v-if="Object.keys(schema.competitor_coverage || {}).length" class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Competitor Schema Coverage
          </h4>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(data, type) in schema.competitor_coverage"
              :key="type"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
              :class="typeInSelected(type) ? 'bg-primary-50 border border-primary-200 dark:bg-primary-900/20 dark:border-primary-700' : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'">
              <span :class="typeInSelected(type) ? 'text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'">{{ type }}</span>
              <span class="text-xs text-gray-400">{{ data.count }}/{{ data.total }} competitors</span>
              <svg v-if="typeInSelected(type)" class="h-3.5 w-3.5 text-primary-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>
            </div>
          </div>
        </div>

        <!-- Selected Types -->
        <div class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Schema Types ({{ schema.selected_types?.length }})
          </h4>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="type in schema.selected_types"
              :key="type"
              class="px-2 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
              {{ type }}
            </span>
          </div>
        </div>

        <!-- Validation Results -->
        <div v-if="schema.validation_result" class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Validation
          </h4>
          <div v-if="schema.validation_result.valid" class="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
            All {{ schema.validation_result.total_blocks }} blocks pass validation
          </div>
          <div v-else class="space-y-1">
            <div v-for="(issue, i) in schema.validation_result.issues" :key="i" class="flex items-center gap-2 text-sm text-amber-600">
              <svg class="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
              {{ issue.type }}: {{ issue.issue }}
            </div>
          </div>
        </div>

        <!-- User Inputs -->
        <div v-if="schema.status === 'ready'" class="mb-4">
          <details class="text-sm">
            <summary class="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
              Customize Author / Publisher
            </summary>
            <div class="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Author Name</label>
                <input v-model="userInputs.author_name" type="text" class="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="e.g. John Smith" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Author Credentials</label>
                <input v-model="userInputs.author_credentials" type="text" class="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Licensed Contractor" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Publisher Name</label>
                <input v-model="userInputs.publisher_name" type="text" class="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Sun Tint LLC" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Logo URL</label>
                <input v-model="userInputs.logo_url" type="text" class="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
              </div>
            </div>
            <button
              @click="saveInputs"
              :disabled="loading"
              class="mt-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 disabled:opacity-50">
              Re-generate with changes
            </button>
          </details>
        </div>

        <!-- Schema Preview -->
        <div v-for="(block, i) in schema.combined_schema" :key="i" class="mb-3">
          <details class="text-sm border border-gray-200 rounded-lg">
            <summary class="cursor-pointer px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg font-medium text-gray-700 dark:text-gray-300">
              {{ block['@type'] }}
              <span class="text-xs text-gray-400 ml-2">{{ Object.keys(block).filter(k => !k.startsWith('@')).length }} properties</span>
            </summary>
            <pre class="p-3 text-xs overflow-x-auto bg-white dark:bg-gray-800 rounded-b-lg">{{ JSON.stringify(block, null, 2) }}</pre>
          </details>
        </div>

        <!-- Approve button -->
        <button
          v-if="schema.status === 'ready' && !schema.approved"
          @click="approve"
          :disabled="loading"
          class="mt-2 px-4 py-2 bg-[#12e40b] text-white rounded-lg text-sm font-medium hover:bg-[#0fd609] disabled:opacity-50">
          Approve Schema
        </button>
      </div>

      <!-- Empty state -->
      <div v-if="!schema && !generating" class="text-sm text-gray-500 dark:text-gray-400">
        Generate production-ready JSON-LD structured data matching competitor schema coverage.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { generatePageSchema, getPageSchema, updateSchemaInputs, approvePageSchema } from '~/javascript/api/schema_generation'

const props = defineProps({
  pageId: { type: String, required: true }
})

const schema = ref(null)
const loading = ref(false)
const generating = ref(false)
const progressMessage = ref('')
const userInputs = ref({})
let subscription = null
let pollTimer = null

const canGenerate = computed(() => {
  if (generating.value || loading.value) return false
  return !schema.value || schema.value.status === 'pending' || schema.value.status === 'failed'
})

function typeInSelected(type) {
  return (schema.value?.selected_types || []).includes(type)
}

async function fetchSchema() {
  try {
    const { data } = await getPageSchema(props.pageId)
    schema.value = data.schema
    if (data.schema?.user_inputs) {
      userInputs.value = { ...data.schema.user_inputs }
    }
    if (data.schema?.status === 'pending' || data.schema?.status === 'selecting' || data.schema?.status === 'generating') {
      generating.value = true
    }
  } catch (e) {
    console.error('Failed to fetch schema:', e)
  }
}

async function startGeneration() {
  loading.value = true
  generating.value = true
  progressMessage.value = 'Starting schema generation...'
  try {
    await generatePageSchema(props.pageId)
    startPolling()
  } catch (e) {
    generating.value = false
    console.error('Failed to start schema generation:', e)
  } finally {
    loading.value = false
  }
}

async function saveInputs() {
  loading.value = true
  generating.value = true
  progressMessage.value = 'Re-generating with updated inputs...'
  try {
    await updateSchemaInputs(props.pageId, userInputs.value)
    startPolling()
  } catch (e) {
    generating.value = false
    console.error('Failed to update inputs:', e)
  } finally {
    loading.value = false
  }
}

async function approve() {
  loading.value = true
  try {
    const { data } = await approvePageSchema(props.pageId)
    schema.value = data.schema
  } catch (e) {
    console.error('Failed to approve schema:', e)
  } finally {
    loading.value = false
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const { data } = await getPageSchema(props.pageId)
      if (data.schema && !['pending', 'selecting', 'generating'].includes(data.schema.status)) {
        schema.value = data.schema
        generating.value = false
        if (data.schema.user_inputs) {
          userInputs.value = { ...data.schema.user_inputs }
        }
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function setupCable() {
  subscription = consumer.subscriptions.create(
    { channel: 'ContentOptimizationChannel', page_id: props.pageId },
    {
      received(data) {
        if (['selecting', 'generating', 'validating'].includes(data.step)) {
          progressMessage.value = data.message || ''
        } else {
          generating.value = false
          stopPolling()
          fetchSchema()
        }
      }
    }
  )
}

onMounted(() => {
  fetchSchema()
  setupCable()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
